import React, { useState, useEffect } from 'react';
import { getUserCurrencyRewards } from '../../DatabaseAPI';

const WeeklyResultsPopup = ({ resultsData, onClose, onMarkViewed, onNavigateToStandings }) => {
    const [currencyRewards, setCurrencyRewards] = useState({});
    const [rewardsLoading, setRewardsLoading] = useState(false);
    const [hoveredLeagueId, setHoveredLeagueId] = useState(null);
    
    // Add diagnostic logging
    console.log('WeeklyResultsPopup: Received resultsData:', resultsData);
    
    // Transform data into league-grouped structure
    const leagueResults = resultsData?.map(league => {
        console.log('WeeklyResultsPopup: Processing league:', {
            league_id: league.league_id,
            league_name: league.league_name,
            has_teams: !!league.teams,
            teams_length: league.teams?.length,
            teams_data: league.teams
        });
        
        return {
            league_id: league.league_id,
            league_name: league.league_name,
            season_year: league.season_year,
            week: league.week,
            team: league.teams?.[0], // User has one team per league - safe access
        };
    }) || [];
    
    console.log('WeeklyResultsPopup: Transformed leagueResults:', leagueResults);
    
    // Fetch currency rewards for each team
    useEffect(() => {
        const fetchCurrencyRewards = async () => {
            if (!resultsData || !leagueResults.length) return;
            
            setRewardsLoading(true);
            
            try {
                console.log('WeeklyResultsPopup: Fetching currency rewards for teams');
                
                const rewardPromises = leagueResults
                    .filter(league => league.team) // Only fetch rewards for leagues with team data
                    .map(async (league) => {
                        try {
                            const response = await getUserCurrencyRewards(
                                league.team.team_id,
                                league.season_year,
                                league.week
                            );
                            return {
                                teamId: league.team.team_id,
                                rewards: response
                            };
                        } catch (error) {
                            console.error(`WeeklyResultsPopup: Error fetching rewards for team ${league.team.team_id}:`, error);
                            return {
                                teamId: league.team.team_id,
                                rewards: null,
                                error: error.message
                            };
                        }
                    });
                
                const rewardResults = await Promise.all(rewardPromises);
                
                const rewardsMap = {};
                rewardResults.forEach(result => {
                    rewardsMap[result.teamId] = result.rewards || {
                        total_rewards: 0,
                        earnings: 0,
                        bonus: 0,
                        eligible_for_bonus: false,
                        error: result.error
                    };
                });
                
                setCurrencyRewards(rewardsMap);
                console.log('WeeklyResultsPopup: Currency rewards loaded:', rewardsMap);
                
            } catch (error) {
                console.error('WeeklyResultsPopup: Error fetching currency rewards:', error);
            } finally {
                setRewardsLoading(false);
            }
        };
        
        fetchCurrencyRewards();
    }, [resultsData, leagueResults.length]);
    
    const handleClose = async () => {
        // Mark all results as viewed before closing
        if (resultsData) {
            try {
                const markPromises = resultsData.map(league =>
                    onMarkViewed(league.league_id, league.season_year, league.week)
                );
                await Promise.all(markPromises);
            } catch (error) {
                console.error('Error marking results as viewed:', error);
            }
        }
        onClose();
    };
    
    const handleLeagueClick = async (league) => {
        console.log('WeeklyResultsPopup: League clicked:', league.league_name);
        
        try {
            // Mark this league as viewed
            await onMarkViewed(league.league_id, league.season_year, league.week);
            
            // Store league context in sessionStorage for navigation
            sessionStorage.setItem('selectedLeagueId', league.league_id);
            if (league.team?.team_id) {
                sessionStorage.setItem('selectedTeamId', league.team.team_id);
            }
            sessionStorage.setItem('previousLocation', 'weeklyResults');
            
            // Navigate to standings page if callback provided
            if (onNavigateToStandings) {
                onNavigateToStandings(league.league_id);
            }
            
            // Close the popup
            onClose();
        } catch (error) {
            console.error('WeeklyResultsPopup: Error handling league click:', error);
        }
    };
    
    const getStandingText = (standing) => {
        if (standing === 1) return '1ST PLACE';
        if (standing === 2) return '2ND PLACE';
        if (standing === 3) return '3RD PLACE';
        return `${standing}TH PLACE`;
    };
    
    const getStandingColor = (standing) => {
        if (standing === 1) return '#FFD700'; // Gold
        if (standing === 2) return '#C0C0C0'; // Silver
        if (standing === 3) return '#CD7F32'; // Bronze
        return '#9CA3AF'; // Gray
    };
    
    // Don't render if no data
    if (!resultsData || leagueResults.length === 0) {
        return null;
    }
    
    return (
        <>
            <style>
                {`
                    @keyframes fadeIn {
                        from {
                            opacity: 0;
                            transform: scale(0.95);
                        }
                        to {
                            opacity: 1;
                            transform: scale(1);
                        }
                    }
                    
                    .weekly-results-overlay {
                        animation: fadeIn 0.2s ease-out;
                    }
                    
                    .results-scroll-container::-webkit-scrollbar {
                        width: 8px;
                    }
                    
                    .results-scroll-container::-webkit-scrollbar-track {
                        background: #1a202c;
                        border-radius: 4px;
                    }
                    
                    .results-scroll-container::-webkit-scrollbar-thumb {
                        background: #4a5568;
                        border-radius: 4px;
                    }
                    
                    .results-scroll-container::-webkit-scrollbar-thumb:hover {
                        background: #4299e1;
                    }
                `}
            </style>
            
            <div className="weekly-results-overlay" style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 10000,
                padding: '20px 10px' // Add padding to prevent edge cutoff
            }}>
                <div style={{
                    backgroundColor: '#1a202c',
                    borderRadius: '12px',
                    padding: '20px',
                    maxWidth: '600px',
                    width: '100%',
                    maxHeight: 'calc(100vh - 40px)', // Account for overlay padding
                    minHeight: '200px', // Ensure minimum usable height
                    color: 'white',
                    position: 'relative',
                    border: '2px solid #4a5568',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden' // Prevent content from escaping
                }}>
                    {/* Sticky Header - Always Visible */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '16px',
                        flexShrink: 0, // Prevent header from shrinking
                        position: 'sticky',
                        top: 0,
                        backgroundColor: '#1a202c',
                        zIndex: 1,
                        paddingBottom: '8px'
                    }}>
                        <h1 style={{
                            fontSize: 'clamp(20px, 5vw, 28px)', // Responsive font size
                            fontWeight: 'bold',
                            color: '#48bb78',
                            margin: 0,
                            fontFamily: '"Bebas Neue", sans-serif',
                            lineHeight: 1.2
                        }}>
                            WEEKLY RESULTS
                        </h1>
                        
                        {/* Close Button - Touch-Friendly */}
                        <button
                            onClick={handleClose}
                            aria-label="Close weekly results"
                            style={{
                                background: 'none',
                                border: '2px solid transparent',
                                color: '#9CA3AF',
                                fontSize: '32px',
                                cursor: 'pointer',
                                padding: '8px',
                                borderRadius: '4px',
                                lineHeight: 1,
                                transition: 'all 0.2s ease',
                                minWidth: '44px', // Touch target minimum
                                minHeight: '44px', // Touch target minimum
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0 // Prevent button from shrinking
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.color = 'white';
                                e.target.style.borderColor = '#4a5568';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.color = '#9CA3AF';
                                e.target.style.borderColor = 'transparent';
                            }}
                        >
                            ×
                        </button>
                    </div>
                    
                    {/* Scrollable League List */}
                    <div className="results-scroll-container" style={{
                        flex: '1 1 auto', // Take remaining space
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        paddingRight: '8px',
                        minHeight: 0 // Allow flex shrinking
                    }}>
                        {leagueResults.map((league) => {
                            // Defensive check: skip leagues without team data
                            if (!league.team) {
                                console.warn('WeeklyResultsPopup: Skipping league without team data:', league);
                                return null;
                            }
                            
                            const rewards = currencyRewards[league.team.team_id];
                            const isHovered = hoveredLeagueId === league.league_id;
                            
                            return (
                                <div
                                    key={league.league_id}
                                    onClick={() => handleLeagueClick(league)}
                                    onMouseEnter={() => setHoveredLeagueId(league.league_id)}
                                    onMouseLeave={() => setHoveredLeagueId(null)}
                                    style={{
                                        backgroundColor: '#2d3748',
                                        border: `2px solid ${isHovered ? '#4299e1' : '#4a5568'}`,
                                        borderRadius: '8px',
                                        padding: '16px',
                                        marginBottom: '12px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
                                        boxShadow: isHovered ? '0 4px 12px rgba(66, 153, 225, 0.3)' : 'none',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: '16px'
                                    }}
                                >
                                    {/* Left: League Name and Placement */}
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        flex: '1 1 auto',
                                        minWidth: 0,
                                        flexWrap: 'wrap'
                                    }}>
                                        {/* League Name */}
                                        <div style={{
                                            fontSize: 'clamp(12px, 3vw, 14px)',
                                            color: '#9CA3AF',
                                            display: 'flex',
                                            alignItems: 'baseline',
                                            gap: '4px',
                                            flex: '1 1 auto',
                                            minWidth: 0
                                        }}>
                                            <span style={{ flexShrink: 0 }}>League:</span>
                                            <span style={{
                                                color: 'white',
                                                fontWeight: '500',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                wordBreak: 'break-word',
                                                lineHeight: '1.3'
                                            }}>
                                                {league.league_name}
                                            </span>
                                        </div>
                                        
                                        {/* Placement */}
                                        <div style={{
                                            color: getStandingColor(league.team.final_standing),
                                            fontWeight: 'bold',
                                            fontSize: 'clamp(14px, 4vw, 18px)',
                                            fontFamily: '"Bebas Neue", sans-serif',
                                            whiteSpace: 'nowrap',
                                            flexShrink: 0
                                        }}>
                                            {getStandingText(league.team.final_standing)}
                                        </div>
                                    </div>
                                    
                                    {/* Right: Mythril and Score */}
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'clamp(8px, 2vw, 16px)',
                                        flex: '0 0 auto',
                                        flexShrink: 0
                                    }}>
                                        {/* Mythril */}
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            backgroundColor: '#1a202c',
                                            padding: '4px 8px',
                                            borderRadius: '4px'
                                        }}>
                                            {rewardsLoading ? (
                                                <span style={{
                                                    color: '#9CA3AF',
                                                    fontSize: 'clamp(12px, 3vw, 16px)',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    💎 ...
                                                </span>
                                            ) : (
                                                <span style={{
                                                    color: rewards?.total_rewards > 0 ? '#ffd700' : '#666',
                                                    fontWeight: 'bold',
                                                    fontSize: 'clamp(12px, 3vw, 16px)',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    💎 {rewards?.total_rewards || 0}
                                                </span>
                                            )}
                                        </div>
                                        
                                        {/* Total Score */}
                                        <div style={{
                                            color: '#48bb78',
                                            fontSize: 'clamp(12px, 3vw, 16px)',
                                            fontWeight: '600',
                                            whiteSpace: 'nowrap',
                                            backgroundColor: '#1a202c',
                                            padding: '4px 8px',
                                            borderRadius: '4px'
                                        }}>
                                            {league.team.total_score.toFixed(1)} pts
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    
                    {/* Empty State */}
                    {leagueResults.length === 0 && (
                        <div style={{
                            textAlign: 'center',
                            padding: '40px 20px',
                            color: '#9CA3AF'
                        }}>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏈</div>
                            <div style={{ fontSize: '16px' }}>No weekly results to display</div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default WeeklyResultsPopup;